// Marketing scroll reveal.
//
// Two halves, deliberately split:
//
//   1. `revealInitScript` runs synchronously in <head>, before anything paints,
//      and marks <html> with `reveal-init`. Without it the server-rendered
//      sections would paint visible, then snap hidden the moment the observer
//      mounted — a visible flicker on every page load.
//
//   2. `useReveals` adds `is-revealed` on first intersection, then unobserves.
//      Reveals never replay when scrolling back up, which is the difference
//      between "settling into place" and a fairground ride.
//
// Fail-safe by construction: if JS never runs, `reveal-init` is never added, so
// every section stays visible. Nothing can be stranded off-screen by a failure
// in this file. The same is true for reduced-motion, handled in CSS.
//
// One IntersectionObserver is shared by every section on the page.

import { useEffect } from 'react'
import { useLocation } from '@tanstack/react-router'

/**
 * Static string (not Function.toString) so build tooling cannot mangle it —
 * same approach as `themeInitScript`.
 */
export const revealInitScript = `(function(){try{if(!window.matchMedia||!window.matchMedia("(prefers-reduced-motion: reduce)").matches){if("IntersectionObserver" in window){document.documentElement.classList.add("reveal-init")}}}catch(e){}})()`

let observer: IntersectionObserver | null = null

function getObserver(): IntersectionObserver | null {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return null
  if (!observer) {
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          entry.target.classList.add('is-revealed')
          // Reveal once. Never replay on scroll-up.
          observer?.unobserve(entry.target)
        }
      },
      {
        // The huge top margin is load-bearing, not a typo. Without it a section
        // that goes from below the viewport to above it in a single jump — End
        // key, scrollbar drag, a skip link — is never "intersecting" at any
        // observed moment, so it would stay at opacity 0 forever. Extending the
        // root far upward means "already scrolled past" counts as intersecting
        // and therefore reveals. The -8% bottom keeps the entrance trigger.
        rootMargin: '100000px 0px -8% 0px',
        threshold: 0.05,
      },
    )
  }
  return observer
}

/**
 * Observe every `[data-reveal]` on the page, and re-scan after client-side
 * navigation (which swaps the sections without a document load).
 */
export function useReveals() {
  const pathname = useLocation({ select: (l) => l.pathname })

  useEffect(() => {
    const io = getObserver()
    if (!io) return
    const watched = new Set<HTMLElement>()
    const watchOne = (el: HTMLElement) => {
      if (watched.has(el) || el.classList.contains('is-revealed')) return
      watched.add(el)
      io.observe(el)
    }
    const watchWithin = (root: ParentNode) => root.querySelectorAll<HTMLElement>('[data-reveal]').forEach(watchOne)
    watchWithin(document)
    // A route's code can load after the path changes (client navigation to a
    // lazily loaded page), so its sections appear later: watch for them too,
    // or they would stay hidden as an empty gap.
    const mo = new MutationObserver((records) => {
      for (const record of records) {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return
          if (node.matches('[data-reveal]')) watchOne(node)
          watchWithin(node)
        })
      }
    })
    mo.observe(document.body, { childList: true, subtree: true })
    return () => {
      mo.disconnect()
      watched.forEach((el) => io.unobserve(el))
    }
  }, [pathname])
}
